import api, {APIResponse} from './APIRequestor'
import {Problem, User, Project, ProblemSolution, MessageThread, Message, Optional, Subscription, BlobUpload} from 'data/types'
import {UserCredentialType} from 'data/enums'

export default class APIService {
  public static getProblem(problemId: number): Promise<APIResponse<Problem>> {
    return api.get<Problem>(`/problems/${problemId}`)
  }

  public static getMe(): Promise<APIResponse<User>> {
    return api.get<User>('/users/me')
  }

  public static login(body: {identifier: string; credential: string; credentialType: UserCredentialType}) {
    return api.post<{jwt: string}>('/auth/login', body)
  }

  public static getProblems(params: {page: number; project_id: number}) {
    return api.get<Problem[]>('/problems', params)
  }

  public static createProblem(input: ProblemInput) {
    return api.post<Problem>('/problems', input)
  }

  public static getProjects() {
    return api.get<Project[]>('/projects')
  }

  public static getProject(projectId: number) {
    return api.get<Project>(`/projects/${projectId}`)
  }

  public static getSelectedProject() {
    return api.get<Optional<Project>>('/project/selected')
  }

  public static setSelectedProject(projectId: Optional<string | number>) {
    return api.patch('/project/selected', {
      projectId: projectId ?? null,
    })
  }

  public static getSolutionsForProblem(problemId: number) {
    return api.get<ProblemSolution[]>(`/problems/${problemId}/solutions`)
  }

  public static createSolutionForProblem(problemId: number, body: SolutionInput) {
    return api.post<ProblemSolution>(`/problems/${problemId}/solution`, body)
  }

  public static archiveSolution(solutionId: number) {
    return api.patch<ProblemSolution>(`/solution/${solutionId}/archive`)
  }

  public static validateProblem(problemId: number) {
    return api.patch<Problem>(`/problems/${problemId}`, {isValidated: true})
  }

  public static archiveProblem(problemId: number) {
    return api.delete(`/problems/${problemId}`)
  }

  public static getThreadDetails(threadId: string) {
    return api.get<MessageThread>(`/chat/threads/${threadId}`)
  }

  public static getMessages(threadId: string, options: {startScope?: string; endScope?: string} = {}) {
    return api.get<Message[]>(`/chat/${threadId}/messages`, {start_scope: options.startScope, end_scope: options.endScope})
  }

  public static postChatMessage(threadId: string, input: Partial<Message>) {
    return api.post<Message>(`/chat/${threadId}/messages`, input)
  }

  public static getRealtimeAuthToken() {
    return api.get<{token: string}>('/realtime/auth')
  }

  public static getBillingPortalLink() {
    return api.get<string>('/billing/portal_link')
  }

  public static getBillingSubscription() {
    return api.get<Optional<Subscription>>('/billing/subscription')
  }

  public static getCheckoutSessionId() {
    return api.get<string>('/billing/checkout')
  }

  public static getConfig() {
    return api.get<any>('/config')
  }
}

export type ProblemInput = {
  projectId: number
  title: string
  description: string
  image: BlobUpload
}

export type SolutionInput = {
  images: BlobUpload[]
  title: string
  description: string
}
